import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import StackedDotswithBars from '../StackedDotswithBars';
import {
    calculateContainerHeight,
    calculateFontSize,
    formatNumWithKMBAlt,
    getConfig,
    scaleMaxValue,
} from 'utils/helperMethods';
import { useContext, useMemo, useRef, useState } from 'react';
// import { extendedBrandMarginData } from './data/marginPercentData_extended';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { groupBarChartWidth } from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import useMarginPercentagebyHierarchy from './hooks/useMarginPercentagebyHierarchy';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import { isEmpty, startCase } from 'lodash';
import { useProfitPoolOverviewContext } from './context/ProfitPoolOverviewContext';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';

const MarginPercentByHierarchyChart = () => {
    const chartHeight = calculateContainerHeight(3);
    const [toLeft2, setToLeft2] = useState(0);
    const chartRef = useRef<any>(null);
    const yAxisGraphWidth = calculateFontSize(window.innerWidth, 60);
    const containerWidth2 = useContainerWidth({
        containerId: 'growth-analysis-sales-infinite-scroll-profit',
    });
    const pointsColorMap: any = {
        CBI: '#9BDE91',
        Distributor: '#8EDFEE',
        Retailer: '#4557D5',
    };

    // Consistent tick count for margin % axis alignment
    const marginPercentTickCount = 8;

    const { selectedSubFilter } = useProfitPoolOverviewContext();
    const { multiSelectedTiers, profitPoolTierList, allDimAndAttributeTypeList } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const {
        tiersColorMapPPOverview: tiersColorMap,
        kpiLabelMapping,
        defaultCurrencySymbol,
        marginPercentChartColors,
    } = profitPoolConfig;

    const updatedTiersColorMap = useMemo(() => {
        const newObj: any = {};
        let i = 0;
        profitPoolTierList.forEach((tier) => {
            newObj[tier.tierName] = marginPercentChartColors[i];
            i++;
        });
        return newObj;
    }, [profitPoolTierList]);

    const graphViewContainerWidth3 = Math.max(16 * groupBarChartWidth, containerWidth2);

    const {
        data: marginPercentageData,
        isPending,
        isError,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        minMarginPercent,
        maxMarginPercent,
        minVolumeSalesEquivalent,
        maxVolumeSalesEquivalent,
    } = useMarginPercentagebyHierarchy();

    const barMinBound = useMemo(() => {
        return minVolumeSalesEquivalent < 0 ? minVolumeSalesEquivalent : 0;
    }, [minVolumeSalesEquivalent]);

    // Prepare Excel export data
    const excelExportData = useMemo(() => {
        if (!marginPercentageData?.length) {
            return { headers: [], data: [] };
        }

        const headers = [
            startCase(selectedSubFilter?.dataKey || 'Attribute'),
            'Tier',
            'Margin %',
            kpiLabelMapping['volume_sales_equivalent'] || 'Depletion Volume',
        ];

        const data = marginPercentageData.map((item: any) => [
            item.attributeDesc || '',
            item.tier || '',
            item.marginPercent || 0,
            item['Depletion Volume'] || 0,
        ]);

        return { headers, data };
    }, [marginPercentageData, selectedSubFilter, kpiLabelMapping]);

    if (isPending || allDimAndAttributeTypeList?.loading) {
        return (
            <div>
                <div>
                    <p className="text-lg font-semibold py-2 mb-2">{`% Margin by ${startCase(
                        selectedSubFilter?.dataKey,
                    )}`}</p>
                </div>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </div>
        );
    }
    if (isError) {
        return (
            <div>
                <div>
                    <p className="text-lg font-semibold py-2 mb-2">{`% Margin by ${startCase(
                        selectedSubFilter?.dataKey,
                    )}`}</p>
                </div>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </div>
        );
    }
    if (isEmpty(marginPercentageData)) {
        return (
            <div>
                <div>
                    <p className="text-lg font-semibold py-2 mb-2">{`% Margin by ${startCase(
                        selectedSubFilter?.dataKey,
                    )}`}</p>
                </div>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </div>
        );
    }
    return (
        <div>
            <div className="flex flex-row items-center justify-between">
                <p className="text-lg font-semibold py-2 mb-2">{`% Margin by ${startCase(
                    selectedSubFilter?.dataKey,
                )}`}</p>
                <div className="flex flex-row gap-x-2 items-center">
                    <CopyToClipBoard refOfComponent={chartRef} />
                    <DownloadExcel
                        title={`% Margin by ${startCase(selectedSubFilter?.dataKey)}`}
                        excelData={excelExportData.data}
                        headers={excelExportData.headers}
                    />
                </div>
            </div>
            <div ref={chartRef}>
                <div className="flex flex-row justify-between my-2 mb-6">
                    <div>
                        <p className="text-sm">{'Margin %'}</p>
                    </div>
                    <div className="flex flex-row gap-2 text-xs items-center">
                        {multiSelectedTiers.map((tier) => (
                            <div key={tier} className="flex flex-row gap-1 items-center">
                                <div
                                    className="w-3 h-2 self-center"
                                    style={{ backgroundColor: tiersColorMap?.[tier] }}
                                />
                                <p>{profitPoolTierList.find((t) => t.tierReference === tier)?.tierName}</p>
                            </div>
                        ))}
                    </div>

                    <div>
                        <p className="text-sm">{`${kpiLabelMapping['volume_sales_equivalent']} `}</p>
                    </div>
                </div>

                <section>
                    <div className="flex">
                        <div className="">
                            <Barchart
                                // padding={[0, 0, 0, 48]}
                                padding={[0, 100, 15, 0]}
                                barWidth={0}
                                xAxisConfig={false}
                                dataset={[]}
                                height={chartHeight}
                                loading={false}
                                chartContainer={`bumpChart-main3`}
                                fields={['attributeDesc', 'abs_profit', 'type']}
                                minBound={minMarginPercent}
                                maxBound={scaleMaxValue(20, maxMarginPercent)}
                                width={50}
                                colorKey={'type'}
                                YGrid={false}
                                yAxisConfig={true}
                                yAxisNice={true}
                                yAxisTickCount={marginPercentTickCount}
                                yAxisLabelFormatter={(val: any) => {
                                    return `${val}%`;
                                }}
                            />
                        </div>

                        <HorizontalInfiniteScroller
                            id={`pint-chart-container`}
                            hasMore={hasNextPage}
                            scrollThreshold={0.9}
                            toScrollWidth={toLeft2}
                            onNext={(width: number) => {
                                // console.log'logging width', width);
                                !isFetchingNextPage && fetchNextPage();
                                // width > 0 && setToLeft2(toLeft2 + width);
                            }}
                        >
                            <div id="pint-chart-container" className="overflow-x-auto max-w-full pb-[60px]">
                                <StackedDotswithBars
                                    containerName="stack-margin-perc"
                                    dataSet={marginPercentageData as any}
                                    height={chartHeight}
                                    width={Math.max(
                                        graphViewContainerWidth3 * (marginPercentageData?.length / 30 || 1),
                                        graphViewContainerWidth3,
                                    )}
                                    xField={'attributeDesc'}
                                    pointsField="marginPercent"
                                    pointMin={minMarginPercent}
                                    pointMax={scaleMaxValue(20, maxMarginPercent)}
                                    pointColorField={'tier'}
                                    barField="Depletion Volume"
                                    barMin={barMinBound}
                                    barMax={scaleMaxValue(20, maxVolumeSalesEquivalent)}
                                    pointsColorMap={updatedTiersColorMap}
                                    loading={isFetchingNextPage}
                                    isPointPercentKPI={true}
                                    xLabelFontSize={11}
                                    pointTickCount={marginPercentTickCount}
                                />
                            </div>
                        </HorizontalInfiniteScroller>

                        <div className="">
                            <Barchart
                                padding={[0, 0, 15, 20]}
                                barWidth={10}
                                xAxisConfig={false}
                                dataset={[]}
                                height={chartHeight}
                                loading={false}
                                chartContainer={`bumpChart-main4`}
                                fields={['attributeDesc', 'abs_profit', 'type']}
                                minBound={barMinBound}
                                maxBound={scaleMaxValue(20, maxVolumeSalesEquivalent)}
                                width={50}
                                colorKey={'type'}
                                YGrid={true}
                                yAxisConfig={true}
                                yAxisNice={false}
                                yAxisLabelFormatter={(val: any) => {
                                    return formatNumWithKMBAlt(val);
                                }}
                            />
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};
export default MarginPercentByHierarchyChart;
